/**
 * Terrain grounding (Graphics G1).
 *
 * The height of the RENDERED terrain at a world (x,z) on the Commons —
 * `meshHeight` reproduces the chunk mesh's grid interpolation exactly, so the
 * avatar clamp, teleports, and seated models agree with the trimesh colliders
 * and the visible ground (the analytic field floats mid-cell on steep terrain).
 * Server-unchanged: grounding is computed client-side from the replicated seed.
 */
import { meshHeight } from './terrain_stamp.js';
import { presetGroundY, type WorldPresetKey } from './personal_presets.js';
import { personalGroundY } from './personal_terrain.js';
import type { DbConnection } from '../module_bindings/index.js';

/** Rendered-ground height (meters) at a world (x,z) on the Commons; 0 if the Commons isn't loaded. */
export function groundY(conn: DbConnection, x: number, z: number): number {
  const commons = [...conn.db.world.iter()].find((w) => w.kind === 'commons');
  if (!commons) return 0;
  return meshHeight(commons.terrainSeed, x, z);
}

/**
 * Rendered-ground height at (x,z) in a SPECIFIC world — Commons chunks use
 * the chunk-grid `meshHeight`, personal worlds the eroded per-seed grid
 * (`personalGroundY`), so the avatar clamp always matches the visible mesh
 * regardless of which world the player is standing in.
 *
 * Perf: this runs (at least) twice per movement tick, and iterating every
 * `world` row per call allocates + scans O(worlds). kind/terrainSeed are
 * immutable after spawn (a Commons debug reseed is the rare exception), so
 * the row resolve is memoized with a short TTL.
 */
const worldMemo = new Map<bigint, { kind: string; seed: string; at: number }>();
const WORLD_MEMO_TTL_MS = 5000;

export function groundYIn(conn: DbConnection, worldId: bigint | null, x: number, z: number): number {
  if (worldId === null) return groundY(conn, x, z);
  const now = Date.now();
  let m = worldMemo.get(worldId);
  if (!m || now - m.at > WORLD_MEMO_TTL_MS) {
    let row: { kind: string; terrainSeed: string } | undefined;
    for (const w of conn.db.world.iter()) {
      if (w.worldId === worldId) { row = w; break; }
    }
    if (!row) return groundY(conn, x, z);
    m = { kind: row.kind, seed: row.terrainSeed, at: now };
    worldMemo.set(worldId, m);
  }
  if (m.kind === 'commons') return meshHeight(m.seed, x, z);
  // The Merchant Hive is FLAT artificial ground — a constant plane at Y 0.
  if (m.kind === 'merchant') return 0;
  // A STYLED personal world rides its analytic preset ground (the merchant
  // doctrine) — the eroded heightfield only serves the blank canvas.
  if (m.kind === 'personal') {
    try {
      const pr = (conn.db as unknown as { worldPreset?: { worldId: { find(id: bigint): { preset: string } | null | undefined } } }).worldPreset?.worldId.find(worldId);
      if (pr && pr.preset !== 'blank') return presetGroundY(pr.preset as WorldPresetKey, x, z);
    } catch { /* pre-publish server */ }
  }
  return personalGroundY(m.seed, x, z);
}
